import { apiFetch } from "./client";

export interface TenantApp {
  id: string;
  companyId: string;
  builtByAgentId: string | null;
  appServicePlanId: string | null;
  name: string;
  description: string | null;
  framework: string | null;
  status: string;
  visibility: string;
  appServiceName: string | null;
  appUrl: string | null;
  azureResourceId: string | null;
  resourceGroup: string | null;
  easyAuthEnabled: boolean;
  customDomain: string | null;
  sourceRepo: string | null;
  currentVersion: string | null;
  healthEndpoint: string | null;
  lastHealthCheck: string | null;
  lastHealthStatus: string | null;
  buildPhase: string | null;
  handedOverAt: string | null;
  handoverNotes: string | null;
  adminApprovedPublic: boolean;
  devServerPort: number | null;
  devServerStatus: string | null;
  sourcePath: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAppInput {
  companyId: string;
  name: string;
  description?: string;
  framework?: string;
  builtByAgentId?: string;
  visibility?: "agents-only" | "internal" | "public";
}

export interface AppDeployment {
  id: string;
  appId: string;
  companyId: string;
  version: string | null;
  status: string;
  deployLog: string | null;
  gitSha: string | null;
  triggeredBy: string | null;
  buildDurationMs: number | null;
  startedAt: string;
  completedAt: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActivateAppsResult {
  agent: { id: string; name: string; role: string; status: string };
  message: string;
  existing?: boolean;
}

export const appsApi = {
  list: (companyId: string) =>
    apiFetch<TenantApp[]>(`/apps?companyId=${companyId}`),

  get: (appId: string) =>
    apiFetch<TenantApp>(`/apps/${appId}`),

  create: (data: CreateAppInput) =>
    apiFetch<TenantApp>("/apps", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (appId: string, data: Partial<CreateAppInput> & { status?: string; sourcePath?: string; devServerPort?: number; devServerStatus?: string; metadata?: Record<string, unknown> }) =>
    apiFetch<TenantApp>(`/apps/${appId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  delete: (appId: string) =>
    apiFetch<void>(`/apps/${appId}`, { method: "DELETE" }),

  deploy: (appId: string, data: { version?: string; sourcePath: string; startupCommand?: string; envVars?: Record<string, string> }) =>
    apiFetch<{ message: string; appId: string }>(`/apps/${appId}/deploy`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  promote: (appId: string, data?: { version?: string; sourcePath?: string; startupCommand?: string; runtime?: string; envVars?: Record<string, string> }) =>
    apiFetch<{ message: string; appId: string; sourcePath: string; version: string }>(`/apps/${appId}/promote`, {
      method: "POST",
      body: JSON.stringify(data || {}),
    }),

  registerMcp: (appId: string, data?: { url?: string; name?: string; displayName?: string; description?: string; transportType?: "http" | "sse"; toolSchema?: Record<string, unknown> }) =>
    apiFetch<{ app: TenantApp; appMcp: Record<string, unknown> }>(`/apps/${appId}/mcp/register`, {
      method: "POST",
      body: JSON.stringify(data || {}),
    }),

  assignMcp: (appId: string, data: { agentIds: string[]; tenantMcpId?: string }) =>
    apiFetch<{ tenantMcpId: string; results: Array<{ agentId: string; status: string; error?: string }> }>(`/apps/${appId}/mcp/assign`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listDeployments: (appId: string) =>
    apiFetch<AppDeployment[]>(`/apps/${appId}/deployments`),

  toggleVisibility: (appId: string, visibility: "agents-only" | "internal" | "public") =>
    apiFetch<TenantApp>(`/apps/${appId}/visibility`, {
      method: "POST",
      body: JSON.stringify({ visibility }),
    }),

  stop: (appId: string) =>
    apiFetch<{ status: string }>(`/apps/${appId}/stop`, { method: "POST" }),

  start: (appId: string) =>
    apiFetch<{ status: string }>(`/apps/${appId}/start`, { method: "POST" }),

  checkHealth: (appId: string) =>
    apiFetch<{ status: string; statusCode?: number }>(`/apps/${appId}/health`),

  activate: (companyId: string, vmSize?: string) =>
    apiFetch<ActivateAppsResult>("/apps/activate", {
      method: "POST",
      body: JSON.stringify({ companyId, vmSize }),
    }),

  startDevServer: (appId: string) =>
    apiFetch<{ port: number; status: string; previewUrl: string }>(`/apps/${appId}/dev-server/start`, {
      method: "POST",
    }),

  stopDevServer: (appId: string) =>
    apiFetch<{ status: string }>(`/apps/${appId}/dev-server/stop`, {
      method: "POST",
    }),

  handover: (appId: string, notes?: string) =>
    apiFetch<TenantApp>(`/apps/${appId}/handover`, {
      method: "POST",
      body: JSON.stringify({ notes }),
    }),

  restart: (appId: string) =>
    apiFetch<{ status: string }>(`/apps/${appId}/restart`, { method: "POST" }),

  getEnvVars: (appId: string) =>
    apiFetch<Record<string, string>>(`/apps/${appId}/env`),

  updateEnvVars: (appId: string, vars: Record<string, string>) =>
    apiFetch<Record<string, string>>(`/apps/${appId}/env`, {
      method: "PUT",
      body: JSON.stringify(vars),
    }),
};
